import MenuItem from "./Item";

/**
 * MenuList
 *
 * 순수 프레젠테이션 컴포넌트
 *
 * 책임:
 * - 메뉴 리스트 UI 렌더링
 * - 펼치기/접기 상태 관리
 * - 아이템 렌더링 (날짜, 편집, 삭제, 저장 등)
 *
 * 데이터와 비즈니스 로직은 컨테이너 컴포넌트에서 처리:
 * - GroupMenuList (그룹 목록)
 * - StorageMenuList (보관함 목록)
 */

export interface MenuListProps {
  label: string;
  icon?: "chatline" | "document" | "ailine"; // 메뉴 아이템 아이콘 타입
  items: Array<{
    id: string;
    label: string;
    date?: string;
    showDate?: boolean;
    isStorage?: boolean;
    isCompleted?: boolean;
  }>;
  open?: boolean;
  setOpen?: (open: boolean) => void;
  editingItemId?: string | null;
  isLoading?: boolean;
  hasMore?: boolean;
  observerTarget?: React.RefObject<HTMLDivElement>;
  disabled?: boolean; // 스트리밍 중 아이템 비활성화

  // 핸들러 (optional - 보관함은 일부만 사용)
  onItemClick: (itemId: string) => void;
  onItemEdit?: (itemId: string) => void;
  onItemDelete: (itemId: string) => void;
  onItemSave?: (itemId: string) => void;
  onItemTitleChange?: (itemId: string, newTitle: string) => void;
}

const MenuList: React.FC<MenuListProps> = ({
  label,
  icon = "chatline",
  items = [],
  open = false,
  setOpen,
  editingItemId = null,
  isLoading = false,
  hasMore = false,
  observerTarget,
  disabled = false,
  onItemClick,
  onItemEdit,
  onItemDelete,
  onItemSave,
  onItemTitleChange,
}) => {
  const toggleOpen = () => {
    setOpen(!open);
  };

  if (!open) {
    // Closed state - just show the menu header with chevron pointing down
    return (
      <button
        onClick={toggleOpen}
        className="box-border flex flex-col items-center p-0 w-full cursor-pointer"
      >
        <div className="w-full [&_.menu-chevron]:rotate-90">
          <MenuItem
            type="menuitem"
            label={label}
            icon={icon}
            onClick={toggleOpen}
          />
        </div>
      </button>
    );
  }

  // Open state - show header with chevron pointing up and items
  return (
    <div
      className={`!border-b-[1px] !border-solid !border-[#EEE] box-border flex flex-col items-center w-full`}
    >
      <div className="w-full [&_.menu-chevron]:rotate-[270deg]">
        <MenuItem
          type="menuitem"
          label={label}
          icon={icon}
          onClick={toggleOpen}
        />
      </div>

      {items.map((item, index) => (
        <MenuItem
          key={item.id}
          type="submenuitem"
          label={item.label}
          date={item.showDate || index === 0}
          dateText={item.date}
          onClick={() => onItemClick(item.id)}
          onEdit={onItemEdit ? () => onItemEdit(item.id) : undefined}
          onDelete={() => onItemDelete(item.id)}
          onSave={onItemSave ? () => onItemSave(item.id) : undefined}
          onTitleChange={
            onItemTitleChange
              ? (newTitle) => onItemTitleChange(item.id, newTitle)
              : undefined
          }
          isEditing={editingItemId === item.id}
          isStorage={item.isStorage}
          canSave={item.isCompleted !== false}
          disabled={disabled}
        />
      ))}

      {/* 무한 스크롤 트리거 */}
      {hasMore && observerTarget && (
        <div ref={observerTarget} className="h-4 w-full" />
      )}

      {/* 로딩 인디케이터 */}
      {isLoading && (
        <div className="flex items-center justify-center py-4 w-full">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#6d2ef2]" />
        </div>
      )}
    </div>
  );
};

export default MenuList;
